/**
 * Company: SPHMedia
 * Description: [Event Banner] To entry point based on the props page.
 */
import NDP2026 from "@assets/ndp/sg61.svg";
import ChevronImage from "@assets/video_widget/Chevron.webp";
import { NDP_2026_TAGNAME } from "@config/KeywordConstants";
import { ReactElement } from "react";
import { Link } from "react-router";

type EventBannerProps = {
  /**
   * Test ID used to identify the event banner in tests.
   * @default "event-banner"
   */
  testId?: string;

  /**
   * URL to navigate to when the banner is clicked.
   */
  link: string;

  /**
   * Title displayed in the event banner.
   */
  title: string;
};

/**
 * Renders the chevron icon used in the event banner links.
 *
 * @returns The rendered chevron icon.
 */
const ChevronIcon = () => (
  <img
    alt="toggle-button-chevie"
    height={30}
    width={30}
    className="pl-[5px] inline"
    src={ChevronImage}
  />
);

/**
 * EventBanner
 * @returns The rendered React element
 */
/**
 * Renders an event banner with responsive content and navigation.
 *
 * @param props - The properties used to configure the event banner.
 * @returns The rendered event banner.
 */
export default function EventBanner({
  testId = "event-banner",
  link,
  title,
}: EventBannerProps): ReactElement {
  const isNDP2026 = title === NDP_2026_TAGNAME;
  const linkProps = { reloadDocument: true, target: "_blank", to: link };
  return (
    <div
      data-testid={testId}
      className="bg-gray-500 border-gray-2050 border-y w-full text-md font-bold leading-6 lg:leading-9 flex justify-items-start lg:justify-center items-center h-[52px] lg:h-[45px] my-5 px-[15px] py-2.5"
    >
      {isNDP2026 ? (
        <>
          <img
            alt="toggle-button-chevie"
            height={40}
            width={40}
            className="pl-[5px] inline"
            src={NDP2026}
          />
          <div className="text-red-100 text-xs md:text-md font-semibold leading-[135%] pl-2.5 ">
            <Link {...linkProps} className="underline decoration-1">
              2026 தேசிய தினம் தொடர்பான அண்மைச் செய்திகளை இங்கே பெறுங்கள்
            </Link>
          </div>
        </>
      ) : (
        <>
          <div className="hidden lg:block pr-2.5 text-gray-100">{title}</div>
          <div className="block lg:hidden pr-2.5 text-red-100 ">
            <Link {...linkProps}>
              {title} செய்திகள்
              <ChevronIcon />
            </Link>
          </div>

          <div className="text-red-100 hidden lg:block ">
            <Link {...linkProps}>
              அண்மைய செய்திகளைப் படிக்க
              <ChevronIcon />
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
